import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { useI18n } from "@/context/I18nContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Share2, Leaf } from "lucide-react";
import { toast } from "sonner";

export function QuoteImageDialog({ open, onOpenChange, text }) {
  const { t } = useI18n();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const render = () => toPng(ref.current, { pixelRatio: 3, cacheBust: true });

  const download = async () => {
    if (!ref.current) return;
    setBusy(true);
    try {
      const dataUrl = await render();
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = "evermind-quote.png";
      a.click();
    } catch {
      toast.error("Couldn't create the image just now.");
    } finally { setBusy(false); }
  };

  const share = async () => {
    if (!ref.current) return;
    setBusy(true);
    try {
      const dataUrl = await render();
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "evermind-quote.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "EverMind" });
      } else {
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = "evermind-quote.png";
        a.click();
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Sharing isn't available here — try Download.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm rounded-3xl" data-testid="quote-image-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{t("favorites.imageTitle")}</DialogTitle>
          <DialogDescription>{t("favorites.imageDesc")}</DialogDescription>
        </DialogHeader>
        <div className="flex justify-center">
          <div
            ref={ref}
            data-testid="quote-image-canvas"
            style={{
              width: 320, height: 320,
              background: "linear-gradient(145deg, #f4ebe1 0%, #ead9c9 55%, #dcc0a9 100%)",
              color: "#3a2e26",
            }}
            className="relative flex flex-col justify-between overflow-hidden rounded-2xl p-7"
          >
            <div className="flex items-center gap-2" style={{ color: "#8a5a3c" }}>
              <img src="/logo-a.png" alt="" style={{ width: 28, height: 28, borderRadius: 999, objectFit: "cover" }} />
              <span className="font-serif text-lg font-semibold">EverMind</span>
            </div>
            <p className="font-serif text-xl italic leading-snug" style={{ color: "#3a2e26" }}>
              “{text}”
            </p>
            <div className="flex items-center gap-1.5 text-xs" style={{ color: "#8a5a3c" }}>
              <Leaf style={{ width: 14, height: 14 }} strokeWidth={1.5} /> evermindpath.com
            </div>
          </div>
        </div>
        <div className="mt-2 flex gap-2">
          <Button onClick={download} disabled={busy} variant="outline" className="flex-1 rounded-full gap-2" data-testid="quote-image-download">
            <Download className="h-4 w-4" strokeWidth={1.5} /> {t("favorites.download")}
          </Button>
          <Button onClick={share} disabled={busy} className="flex-1 rounded-full gap-2" data-testid="quote-image-share">
            <Share2 className="h-4 w-4" strokeWidth={1.5} /> {t("favorites.shareImage")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
